import { BackButtonCWUI } from '@next-order/next-order-components';
import React, { useEffect, useMemo, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getItemPrice, getItemsInCart } from '@next-order/next-order-engine';
import { toggleMobileCart, voucherFilterView } from 'containers/Home/actions';
import getImage from 'helpers/getImage';
import {
  buildBogoEstimateRows,
  excludeBogoEditRows,
} from 'helpers/bogoVoucherHelper';
import {
  detectBogoDiscount,
  getBogoSpendProgress,
} from 'helpers/order-engine/getBogoVoucherDiscount';
import './BogoVoucherView.css';

/**
 * BOGO voucher (type "4") Buy/Get item picker.
 *
 * Separate flow from the type-3 VoucherSpecialBox (which stays untouched):
 * shown when a BOGO voucher is applied but the cart cannot satisfy the deal.
 * Renders a "Buy" section (buyConfig.quantity slots) and a "Get" section
 * (getConfig.quantity slots). Spend-trigger vouchers start with one Buy slot
 * of matching items; while the cart plus the Buy picks is still short of
 * minSpend, another Buy slot is added after each pick. Clicking a slot filters the menu (same
 * voucherFilterView plumbing as type 3); once every slot is filled the
 * selected items are added to the order and the voucher is applied.
 */
export default function BogoVoucherView({
  voucherItem,
  selectedSpecials,
  addBogoVoucherItemsToOrder,
  hideOrder,
  storeConfig,
  sidebarAnimationClass,
}) {
  const dispatch = useDispatch();
  const activeBoxIndex = useSelector(
    (state) => state.homeReducer.voucherFilter.voucherBoxIndex
  );
  const halfHalfSetup = useSelector(
    (state) => state.layoutReducer.halfHalfSetup
  );
  const currentOrder = useSelector((state) => state.homeReducer.currentOrder);
  const currency = storeConfig?.currency || '';

  const sections = useMemo(
    () => voucherItem?.bogoSections || [],
    [voucherItem?.bogoSections]
  );
  const spendSection = sections.find((section) => section.isSpend);
  const getQuantity =
    sections.find((section) => section.key === 'get')?.quantity || 0;

  // Spend deals are judged against everything already in the cart plus the
  // items picked here.
  const editUnits = voucherItem?.editUnits;
  const cartRows = useMemo(
    () =>
      spendSection
        ? excludeBogoEditRows(
            getItemsInCart(currentOrder || {}).filter(
              (row) => !row.isSecondHalf
            ),
            editUnits
          )
        : [],
    [spendSection, currentOrder, editUnits]
  );
  const keyOffset = cartRows.reduce(
    (max, row) => Math.max(max, Number(row.primaryKey) || 0),
    0
  );

  // Spend progress counts the cart plus the Buy picks — the reward pick never
  // counts toward minSpend.
  const buySelectionIndexes = useMemo(
    () =>
      spendSection
        ? [
            ...new Set(
              (selectedSpecials || [])
                .filter(
                  (item) =>
                    item.voucherId === voucherItem?._id &&
                    item.boxIndex >= getQuantity
                )
                .map((item) => item.boxIndex)
            ),
          ].sort((a, b) => a - b)
        : [],
    [spendSection, selectedSpecials, voucherItem?._id, getQuantity]
  );
  const spendProgress = useMemo(() => {
    if (!spendSection) return null;
    const buySelections = (selectedSpecials || []).filter(
      (item) =>
        item.voucherId === voucherItem?._id && item.boxIndex >= getQuantity
    );
    return getBogoSpendProgress(voucherItem, [
      ...cartRows,
      ...buildBogoEstimateRows(buySelections, keyOffset),
    ]);
  }, [
    spendSection,
    selectedSpecials,
    voucherItem,
    getQuantity,
    cartRows,
    keyOffset,
  ]);
  const isSpendMet = !!spendProgress && spendProgress.remaining <= 0;

  // Global slot list in display order: buy slots first, then get slots.
  const slots = useMemo(() => {
    const list = [];
    if (spendSection) {
      const buyIndexes = [...buySelectionIndexes];
      if (!isSpendMet) {
        buyIndexes.push(
          buyIndexes.length
            ? buyIndexes[buyIndexes.length - 1] + 1
            : getQuantity
        );
      }
      sections.forEach((section) => {
        if (section.isSpend) {
          buyIndexes.forEach((boxIndex, i) =>
            list.push({ boxIndex, section, isFirstOfSection: i === 0 })
          );
        } else {
          for (let i = 0; i < section.quantity; i++) {
            list.push({ boxIndex: i, section, isFirstOfSection: i === 0 });
          }
        }
      });
      return list;
    }
    sections.forEach((section) => {
      for (let i = 0; i < section.quantity; i++) {
        list.push({
          boxIndex: list.length,
          section,
          isFirstOfSection: i === 0,
        });
      }
    });
    return list;
  }, [sections, spendSection, buySelectionIndexes, isSpendMet, getQuantity]);

  const getSelectionForSlot = (boxIndex) =>
    (selectedSpecials || []).find(
      (item) =>
        item.voucherId === voucherItem?._id && item.boxIndex === boxIndex
    );

  const applyFilterView = (slot) => {
    const { section } = slot;
    batch(() => {
      dispatch(toggleMobileCart(false));
      dispatch(
        voucherFilterView({
          isVoucherFilterApplied: true,
          voucherBoxIndex: slot.boxIndex,
          itemExist: getSelectionForSlot(slot.boxIndex) ? slot.boxIndex : -1,
          voucherMenuItems: section.menuItems,
          voucherMenuSizes: section.menuSizes,
          voucherSubModifiers: section.subModifiers,
          voucherMenuCategories: section.categories,
        })
      );
    });
  };

  // Desktop: auto-open the filter for the first empty slot (and auto-advance
  // after each selection) — same behavior the type-3 picker provides.
  const lastAutoAppliedRef = useRef(-1);
  const isSubmittedRef = useRef(false);
  useEffect(() => {
    // After "Add to Order" the picker stays mounted during the close
    // transition — never re-apply the menu filter at that point.
    if (isSubmittedRef.current) return;
    if (!slots.length) return;
    const isDesktop =
      window.innerWidth > 991 &&
      !(window.navigator.userAgent.match(/iPad/i) && window.innerHeight > 1250);
    if (!isDesktop) return;

    const firstEmpty = slots.find(
      (slot) => !getSelectionForSlot(slot.boxIndex)
    );
    if (firstEmpty && lastAutoAppliedRef.current !== firstEmpty.boxIndex) {
      lastAutoAppliedRef.current = firstEmpty.boxIndex;
      applyFilterView(firstEmpty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, selectedSpecials]);

  const filledSelections = slots
    .map((slot) => getSelectionForSlot(slot.boxIndex))
    .filter(Boolean);
  const isSubmitEnabled = filledSelections.length === slots.length;
  const firstEmptySlot = slots.find(
    (slot) => !getSelectionForSlot(slot.boxIndex)
  );

  const getSelectionPrice = (item) =>
    Number(getItemPrice(item, halfHalfSetup)) || 0;

  // Live preview: total of the selected items minus the BOGO discount the
  // engine would grant for exactly these rows.
  const { totalCost, estimatedDiscount } = useMemo(() => {
    let total = 0;
    filledSelections.forEach((item) => {
      total += getSelectionPrice(item);
    });
    const rows = [
      ...cartRows,
      ...buildBogoEstimateRows(filledSelections, keyOffset),
    ];
    const discount = detectBogoDiscount(voucherItem, rows).voucherDiscount;
    return { totalCost: total, estimatedDiscount: discount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecials, voucherItem, halfHalfSetup, cartRows, keyOffset]);

  const payable = Math.max(totalCost - estimatedDiscount, 0);

  const benefit = voucherItem?.benefit || {};
  const benefitType = benefit.benefitType || '3';
  const isFreeBenefit = benefitType !== '1' && benefitType !== '2';
  const rewardNoun = isFreeBenefit ? 'free item' : 'discounted item';

  // Benefit criteria shown next to the "Get" label, e.g. "Free item",
  // "20% discount (max $5)", "$2 discount".
  const getBenefitText = (section) => {
    if (benefitType === '1') {
      const cap = Number(benefit.maxDiscountAmount);
      return `${Number(benefit.discountValue) || 0}% discount${
        cap > 0 ? ` (max ${currency}${cap})` : ''
      }`;
    }
    if (benefitType === '2') {
      return `${currency}${Number(benefit.discountValue) || 0} discount`;
    }
    return section.quantity > 1 ? 'Free items' : 'Free item';
  };

  const getBenefitBadge = () => {
    if (benefitType === '1')
      return `${Number(benefit.discountValue) || 0}% off`;
    if (benefitType === '2')
      return `${currency}${Number(benefit.discountValue) || 0} off`;
    return 'Free';
  };

  const getInstruction = () => {
    if (isSubmitEnabled) return 'All set. Add it to your order.';
    const nextSection = firstEmptySlot?.section;
    if (nextSection?.key === 'get') {
      const buyNames = filledSelections
        .filter((item) => {
          const slot = slots.find((s) => s.boxIndex === item.boxIndex);
          return slot?.section.key !== 'get';
        })
        .map((item) => item.name);
      const intro = buyNames.length
        ? `${buyNames.join(', ')} ${buyNames.length > 1 ? 'are' : 'is'} in. `
        : '';
      return `${intro}Now choose the item you want ${
        isFreeBenefit ? 'free' : 'discounted'
      } from the menu.`;
    }
    if (nextSection?.isSpend) {
      return `Spend ${currency}${nextSection.minSpend} on ${
        nextSection.anyItem
          ? 'anything'
          : nextSection.description || 'selected items'
      }. The reward item does not count.`;
    }
    const buyQuantity = nextSection?.quantity || 1;
    return `Pick ${
      buyQuantity > 1 ? `${buyQuantity} items` : 'an item'
    } from the menu, then choose your ${rewardNoun}.`;
  };

  const getSectionMeta = (section) => {
    if (section.key === 'get') return getBenefitText(section);
    if (section.isSpend) {
      const remaining = spendProgress?.remaining ?? section.minSpend;
      return remaining > 0
        ? `${currency}${Number(remaining).toFixed(2)} to go`
        : 'Spend reached';
    }
    return `Choose ${section.quantity || 1}`;
  };

  const renderSpendProgress = (section) => {
    const minSpend = Number(section.minSpend) || 0;
    const spent = Number(spendProgress?.spent) || 0;
    const percent = minSpend > 0 ? Math.min((spent / minSpend) * 100, 100) : 0;
    return (
      <div className='bogoPanel__spend'>
        <div className='bogoPanel__spendText'>
          {isSpendMet
            ? 'Minimum spend reached. Pick your reward below.'
            : `${currency}${spent.toFixed(2)} of ${currency}${minSpend.toFixed(
                2
              )} spent`}
        </div>
        <div className='bogoPanel__spendTrack'>
          <span
            className='bogoPanel__spendFill'
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    );
  };

  const renderSlot = (slot) => {
    const selection = getSelectionForSlot(slot.boxIndex);
    const isGet = slot.section.key === 'get';
    const isActive = activeBoxIndex === slot.boxIndex;
    const className = `bogoPanel__slot${
      isActive ? ' bogoPanel__slot--active' : ''
    }${selection ? '' : ' bogoPanel__slot--empty'}`;
    const key = `bogo-slot-${slot.section.key}-${slot.boxIndex}`;

    if (!selection) {
      return (
        <button
          type='button'
          key={key}
          className={className}
          onClick={() => applyFilterView(slot)}
        >
          <span className='bogoPanel__slotPlus'>+</span>
          <span className='bogoPanel__slotLabel'>
            {isGet ? `${isFreeBenefit ? 'Free' : 'Reward'} item` : 'Buy item'}
          </span>
        </button>
      );
    }

    const imageUrl = getImage(selection.mediaPath || selection.urlS3);
    return (
      <button
        type='button'
        key={key}
        className={className}
        onClick={() => applyFilterView(slot)}
      >
        {imageUrl && <img src={imageUrl} alt='' />}
        <span className='bogoPanel__slotShade' />
        <span
          className={`bogoPanel__badge ${
            isGet ? 'bogoPanel__badge--reward' : 'bogoPanel__badge--buy'
          }`}
        >
          {isGet ? getBenefitBadge() : 'Buy'}
        </span>
        <span className='bogoPanel__slotFooter'>
          <span
            className={`bogoPanel__slotPrice${
              isGet ? ' bogoPanel__strike' : ''
            }`}
          >
            {currency}
            {getSelectionPrice(selection).toFixed(2)}
          </span>
          <span className='bogoPanel__slotName'>{selection.name}</span>
        </span>
      </button>
    );
  };

  const onFooterClick = () => {
    if (isSubmitEnabled) {
      isSubmittedRef.current = true;
      addBogoVoucherItemsToOrder();
      return;
    }
    if (firstEmptySlot) applyFilterView(firstEmptySlot);
  };

  const title =
    voucherItem?.voucherSubHeading ||
    voucherItem?.description ||
    voucherItem?.voucherCode ||
    voucherItem?.name ||
    '';

  const renderSkeletonSection = (key) => (
    <div key={key} className='bogoPanel__section'>
      <div className='bogoPanel__sectionHeader'>
        <span className='bogoPanelSkeleton bogoPanelSkeleton--sectionTitle' />
        <span className='bogoPanelSkeleton bogoPanelSkeleton--sectionMeta' />
      </div>
      <div className='bogoPanel__slots'>
        <span className='bogoPanelSkeleton bogoPanelSkeleton--slot' />
      </div>
    </div>
  );

  if (voucherItem?.isLoading) {
    return (
      <div className={`bogoPanel ${sidebarAnimationClass || ''}`}>
        <div className='bogoPanel__top'>
          <BackButtonCWUI onClick={hideOrder} />
          <span className='bogoPanel__tag'>Voucher</span>
        </div>
        <div
          className='bogoPanel__body'
          aria-busy='true'
          aria-label='Loading offer'
        >
          <span className='bogoPanelSkeleton bogoPanelSkeleton--title' />
          <span className='bogoPanelSkeleton bogoPanelSkeleton--titleShort' />
          <span className='bogoPanelSkeleton bogoPanelSkeleton--subtitle' />
          <span className='bogoPanelSkeleton bogoPanelSkeleton--subtitleShort' />
          {renderSkeletonSection('bogo-skeleton-buy')}
          {renderSkeletonSection('bogo-skeleton-get')}
        </div>
        <div className='bogoPanel__footer'>
          <div className='bogoPanel__cta bogoPanel__cta--pending'>
            <span className='bogoPanelSkeleton bogoPanelSkeleton--cta' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bogoPanel ${sidebarAnimationClass || ''}`}>
      <div className='bogoPanel__top'>
        <BackButtonCWUI onClick={hideOrder} />
        <span className='bogoPanel__tag'>Voucher</span>
      </div>

      <div className='bogoPanel__body'>
        <h2 className='bogoPanel__title'>{title}</h2>
        <p className='bogoPanel__subtitle'>{getInstruction()}</p>

        {sections.map((section) => {
          const sectionSlots = slots.filter(
            (slot) => slot.section.key === section.key
          );
          return (
            <div
              key={`bogo-section-${section.key}`}
              className='bogoPanel__section'
            >
              <div className='bogoPanel__sectionHeader'>
                <span className='bogoPanel__sectionTitle'>
                  {section.isSpend
                    ? `Spend ${currency}${section.minSpend}`
                    : section.label}
                  {section.quantity > 1 ? ` (${section.quantity})` : ''}
                </span>
                <span className='bogoPanel__sectionMeta'>
                  {getSectionMeta(section)}
                </span>
              </div>
              {section.isSpend && renderSpendProgress(section)}
              <div className='bogoPanel__slots'>
                {sectionSlots.map(renderSlot)}
              </div>
            </div>
          );
        })}
      </div>

      <div className='bogoPanel__footer'>
        <button
          type='button'
          className={`bogoPanel__cta${
            isSubmitEnabled ? '' : ' bogoPanel__cta--pending'
          }`}
          onClick={onFooterClick}
        >
          {isSubmitEnabled ? (
            <>
              {`Add to Order - ${currency}${payable.toFixed(2)}`}
              {estimatedDiscount > 0 && (
                <span className='bogoPanel__ctaStrike'>
                  {currency}
                  {totalCost.toFixed(2)}
                </span>
              )}
            </>
          ) : firstEmptySlot?.section.key === 'get' ? (
            `Choose your ${rewardNoun}`
          ) : (
            'Choose your item'
          )}
        </button>
      </div>
    </div>
  );
}
